import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-affiliates-terms',
  standalone: true,
  imports: [RouterLink],
  template: `
    <main class="min-h-screen bg-stone-950 text-stone-100 py-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-4xl mx-auto">
        <!-- Header -->
        <div class="mb-10 pb-8 border-b border-stone-800">
          <div class="flex items-center gap-3 mb-4">
            <span class="p-2.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <span class="material-symbols-outlined text-2xl">gavel</span>
            </span>
            <span class="text-xs uppercase tracking-widest text-amber-400 font-bold">Legal Agreement</span>
          </div>
          <h1 class="text-3xl sm:text-4xl font-serif font-bold text-white tracking-tight">Affiliate Program Agreement & Payout Terms</h1>
          <p class="mt-3 text-sm text-stone-400">
            Regency House Cleaning LLC — Effective Date: August 2026
          </p>
          <div class="mt-6 flex flex-wrap gap-4 text-xs text-stone-400">
            <a routerLink="/affiliates/join" class="text-amber-400 hover:underline flex items-center gap-1 font-semibold">
              <span class="material-symbols-outlined text-sm">arrow_back</span>
              Back to Partner Registration
            </a>
            <a routerLink="/affiliates/dashboard" class="text-stone-300 hover:underline flex items-center gap-1">
              Partner Dashboard
            </a>
          </div>
        </div>

        <!-- Document Content -->
        <article class="prose prose-invert max-w-none space-y-8 text-stone-300 text-sm leading-relaxed">
          <div class="p-5 rounded-xl bg-stone-900 border border-stone-800 text-stone-300 text-sm">
            <p>
              This Affiliate Program Agreement (the <strong>“Agreement”</strong>) is entered into between <strong>Regency House Cleaning LLC</strong> (<strong>“Regency,” “Company,” “we,”</strong> or <strong>“us”</strong>) and the affiliate identified upon registration (<strong>“Affiliate”</strong>). The parties agree as follows:
            </p>
          </div>

          <!-- Section 1 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">1. Purpose and Relationship</h2>
            <p>
              Regency operates an affiliate and referral program through which approved affiliates may promote eligible Regency House Cleaning services and earn commissions for Qualified Sales. Affiliate participates as an independent contractor. Nothing in this Agreement creates an employment, partnership, joint venture, franchise, fiduciary, or agency relationship, and Affiliate has no authority to bind Regency.
            </p>
          </section>

          <!-- Section 2 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">2. Commission Structure</h2>
            <div class="bg-stone-900/60 p-4 rounded-lg border border-stone-800 space-y-3">
              <h3 class="font-bold text-amber-300 text-sm">Standard Offer</h3>
              <p>Affiliate may earn a flat <strong>$150 USD</strong> commission for each verified new customer acquisition that becomes a Qualified Sale.</p>
              <h3 class="font-bold text-amber-300 text-sm pt-2">High-Tier Offer</h3>
              <p>Affiliate may earn a flat <strong>$100 USD</strong> commission for each completed sale of a Regency-designated high-tier subscription that becomes a Qualified Sale.</p>
            </div>
            <p>
              Regency will identify which services or subscription plans qualify for each commission level. No commission is earned solely because a person clicks a link, submits an inquiry, requests an estimate, schedules an appointment, or provides contact information.
            </p>
          </section>

          <!-- Section 3 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">3. Qualified Sale</h2>
            <p>A <strong>“Qualified Sale”</strong> means a referral that satisfies all of the following criteria:</p>
            <ul class="list-disc pl-5 space-y-2 text-stone-300">
              <li>The customer is a genuine new Regency customer and is not an existing customer, active lead, duplicate referral, or otherwise excluded under Regency program rules.</li>
              <li>The customer is properly attributed to Affiliate through an approved affiliate link, promo code, or tracking method.</li>
              <li>The customer completes the eligible purchase and Regency successfully receives payment.</li>
              <li>The transaction is not fraudulent, duplicated, canceled, refunded, disputed, charged back, defaulted, or otherwise reversed.</li>
              <li>Any applicable trial, statutory cancellation, refund, or verification period has expired.</li>
              <li>The referral and promotion comply with this Agreement and applicable law.</li>
            </ul>
            <p>
              Unless Regency specifies a different qualifying window for a particular offer, a transaction or subscription must remain active and in good standing for <strong>30 days</strong> before the commission becomes final and payable. A commission shown as tracked or pending is not earned until all qualification requirements are satisfied.
            </p>
          </section>

          <!-- Section 4 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">4. Tracking and Attribution</h2>
            <p>Regency may provide Affiliate with a unique referral link, promotional code, tracking identifier, or combination thereof. Unless Regency states otherwise in writing:</p>
            <ul class="list-disc pl-5 space-y-2 text-stone-300">
              <li>Referral cookies use a <strong>30-day attribution window</strong>.</li>
              <li>Attribution follows a <strong>last-click model</strong>.</li>
              <li>The most recent eligible affiliate referral before the qualifying purchase receives attribution.</li>
              <li>Only one affiliate may receive a commission for a single customer transaction.</li>
            </ul>
            <p>
              Regency's tracking and transaction records control absent a demonstrable technical error. Regency is not responsible for attribution failures caused by deleted or blocked cookies, privacy settings, ad blockers, device or browser changes, incorrect promo-code entry, third-party platform limitations, or circumstances outside Regency's reasonable control.
            </p>
          </section>

          <!-- Section 5 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">5. Payment Terms</h2>
            <ul class="list-disc pl-5 space-y-2 text-stone-300">
              <li>Approved commissions are paid on a <strong>Net-30 basis</strong> following the end of the applicable commission period. For example, approved January earnings may be paid by March 1.</li>
              <li><strong>Minimum payout threshold:</strong> $100 USD in approved commissions.</li>
              <li>Balances below $100 roll forward until the threshold is reached.</li>
              <li><strong>Payment methods:</strong> Stripe Payouts or PayPal Payouts API.</li>
              <li>Affiliate must provide accurate payment and tax information and any reasonably required tax forms, including Form W-9 when applicable.</li>
            </ul>
          </section>

          <!-- Section 6 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">6. Refunds, Cancellations, Chargebacks and Defaults</h2>
            <p>
              No commission is payable on a transaction that is refunded, canceled, disputed, charged back, defaulted, determined fraudulent, or otherwise reversed before the commission becomes final.
            </p>
            <p>
              If a commission was credited or paid and the related transaction later becomes ineligible, Regency may deduct that amount from pending or future commissions, offset it against other amounts owed to Affiliate, or request repayment if no sufficient future commission balance exists. Regency may temporarily hold commissions reasonably related to suspected fraud, disputes, duplicate referrals, or other irregularities while they are investigated.
            </p>
          </section>

          <!-- Section 7 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">7. Fraud, Self-Referrals and Manipulation</h2>
            <p>Affiliate may not generate commissions through fraudulent, deceptive, artificial, or manipulated activity. Prohibited conduct includes:</p>
            <ul class="list-disc pl-5 space-y-2 text-stone-300">
              <li>Creating fake or duplicate customer accounts or submitting false customer information.</li>
              <li>Using stolen, unauthorized, or manipulated payment information.</li>
              <li>Cookie stuffing, forced clicks, bots, automated traffic, or manipulation of referral tracking.</li>
              <li>Purchasing through Affiliate's own link or code primarily to generate a commission.</li>
              <li>Coordinating transactions whose primary purpose is generating affiliate commissions.</li>
              <li>Misrepresenting customer identity, eligibility, purchase status, or source.</li>
            </ul>
            <p>
              Regency may reject affected commissions, suspend the Affiliate account, withhold payments reasonably connected to suspected violations during investigation, and terminate participation. Confirmed fraudulent commissions are forfeited.
            </p>
          </section>

          <!-- Section 8 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">8. Promotional Rules and Prohibited Promotion</h2>
            <p>Affiliate must represent Regency accurately, lawfully, and professionally. Affiliate may not:</p>
            <ul class="list-disc pl-5 space-y-2 text-stone-300">
              <li>Make false, deceptive, exaggerated, or unsubstantiated claims about Regency, its services, pricing, availability, results, or guarantees.</li>
              <li>Imply that Affiliate is an employee, owner, agent, or authorized representative of Regency.</li>
              <li>Create unauthorized discounts, offers, coupons, or promotions.</li>
              <li>Send unlawful or unsolicited spam.</li>
              <li>Post affiliate links on generic coupon-scraping or unauthorized coupon sites.</li>
              <li>Bid on or purchase paid-search/PPC keywords containing “Regency House Cleaning,” confusingly similar terms, misspellings, or Regency trademarks without prior written approval.</li>
              <li>Register domains, social-media usernames, listings, or advertising accounts that impersonate or create confusion with Regency.</li>
            </ul>
            <p>
              Regency may require Affiliate to promptly correct or remove promotional content that violates this Agreement, applicable law, or Regency's brand standards.
            </p>
          </section>

          <!-- Section 9 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">9. FTC Endorsement and Advertising Compliance</h2>
            <p>
              Affiliate must comply with all applicable Federal Trade Commission (<strong>“FTC”</strong>) laws, regulations, guidance, and endorsement requirements, including the FTC Endorsement Guides, whenever advertising, recommending, reviewing, endorsing, or otherwise promoting Regency.
            </p>
            <div class="bg-stone-900 border border-stone-800 p-4 rounded-lg space-y-2">
              <h3 class="font-bold text-amber-300">Required Disclosure Examples</h3>
              <p>Disclosures must be clear and conspicuous. Acceptable examples include:</p>
              <ul class="list-disc pl-5 space-y-1 text-stone-300">
                <li>“I earn a commission when you book through my link.”</li>
                <li>“This is a paid partnership with Regency House Cleaning.”</li>
                <li>“I may receive a commission for customers referred through this link.”</li>
                <li>Clear labels such as “Ad,” “Sponsored,” or “Paid partnership.”</li>
              </ul>
            </div>
            <p>
              Affiliate must not hide a required disclosure at the bottom of a long caption, among numerous hashtags or links, on a profile page only, or in another location consumers are unlikely to notice. Serious or repeated FTC violations will result in immediate termination and forfeiture of affected commissions.
            </p>
          </section>

          <!-- Section 10 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">10. Brand and Intellectual Property</h2>
            <p>
              Regency grants Affiliate a limited, revocable, non-exclusive, non-transferable license to use approved Regency names, logos, photographs, graphics, and promotional materials solely to participate in the affiliate program. Affiliate may not materially alter or use Regency branding in a misleading manner. All rights in Regency's brand and intellectual property remain with Regency. Upon termination, Affiliate must stop representing themselves as a Regency affiliate and discontinue unauthorized use of Regency branding.
            </p>
          </section>

          <!-- Section 11 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">11. Customer Relationships and Authority</h2>
            <p>
              Customers referred through the program are customers of Regency. Regency retains sole control over customer acceptance, pricing, estimates, scheduling, service availability and areas, refunds, customer service, subscriptions, cancellations, and services provided. Affiliate may not enter contracts, accept customer payments, issue refunds, make guarantees, or otherwise bind Regency unless expressly authorized in writing.
            </p>
          </section>

          <!-- Section 12 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">12. Program Changes</h2>
            <p>
              Regency may prospectively modify commission rates, eligible services, qualification periods, tracking methods, payment procedures, promotional rules, or other program terms upon reasonable notice. Legitimate commissions already earned before the effective date of a change remain subject to the terms under which they were earned.
            </p>
          </section>

          <!-- Section 13 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">13. Termination</h2>
            <p>
              Either party may terminate this Agreement with seven (7) days' written notice. Regency may suspend or terminate Affiliate immediately for fraud, material breach, unlawful conduct, misuse of Regency's brand, deceptive advertising, serious FTC violations, or conduct reasonably likely to materially harm Regency or its customers.
            </p>
            <p>
              Legitimate commissions earned and finalized before termination remain payable under the normal payment schedule, subject to refunds, chargebacks, fraud review, offsets, and this Agreement. Affiliate will not earn commissions on new referrals generated after the effective termination date.
            </p>
          </section>

          <!-- Section 14 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">14. Confidentiality and Customer Information</h2>
            <p>
              Affiliate must protect confidential or non-public Regency information, including confidential customer information, internal pricing strategies, non-public business information, credentials, and proprietary program information. Any customer information received through the program may be used only for authorized purposes and must be handled in accordance with applicable privacy laws.
            </p>
          </section>

          <!-- Section 15 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">15. Independent Contractor and Taxes</h2>
            <p>
              Affiliate is an independent contractor and is solely responsible for all federal, state, and local taxes, expenses, licenses, and obligations arising from affiliate payments or activities. Affiliate is not entitled to wages, employee benefits, unemployment benefits, workers' compensation, or other employee benefits from Regency.
            </p>
          </section>

          <!-- Section 16 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">16. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, Regency will not be liable for indirect, incidental, special, consequential, exemplary, or lost-profit damages arising from Affiliate's participation in the program. Nothing in this Agreement excludes or limits liability that cannot legally be excluded or limited.
            </p>
          </section>

          <!-- Section 17 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">17. Indemnification</h2>
            <p>
              Affiliate agrees to indemnify and hold harmless Regency House Cleaning LLC and its owners, officers, employees, and agents from third-party claims, losses, liabilities, damages, penalties, and reasonable costs arising from Affiliate's unlawful conduct, material breach of this Agreement, unauthorized representations, infringement of third-party rights, or violation of applicable advertising, privacy, or consumer-protection laws.
            </p>
          </section>

          <!-- Section 18 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">18. Governing Law and Venue</h2>
            <p>
              This Agreement is governed by the laws of the State of New York, without regard to conflict-of-law principles. Any dispute relating to this Agreement shall be brought in a court of competent jurisdiction in the State of New York unless the parties agree in writing to another lawful dispute-resolution procedure.
            </p>
          </section>

          <!-- Section 19 -->
          <section class="space-y-3">
            <h2 class="text-xl font-serif font-bold text-white border-l-2 border-amber-400 pl-3">19. Entire Agreement; Severability; Electronic Signatures</h2>
            <p>
              This Agreement is the entire agreement between Regency and Affiliate regarding the affiliate program and supersedes prior discussions or representations concerning the program. If any provision is unenforceable, the remaining provisions remain in effect to the extent permitted by law. Failure to enforce a provision once does not waive future enforcement. Electronic signatures and electronically accepted copies may be treated as originals to the extent permitted by applicable law.
            </p>
          </section>
        </article>

        <!-- Footer link back -->
        <div class="mt-12 pt-8 border-t border-stone-800 text-center">
          <a routerLink="/affiliates/join" class="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs transition-all">
            <span>Accept Terms & Apply Now</span>
            <span class="material-symbols-outlined text-base">arrow_forward</span>
          </a>
        </div>
      </div>
    </main>
  `,
})
export class AffiliatesTermsComponent { }
